import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import {
  Content,
  Header,
  Heading,
  Main,
  SubHeading,
} from "../commons/Template/Template";
import { Form, FormLoader } from "../commons/Form/Form";
import { Input } from "../commons/Form/Form";
import authAPI, { mapAuthErrors } from "../../api/authentication";
import routes from "../../routes/routes";
import { useToastsDispatch } from "../commons/Toasts/ToastsContext";
import { useKeyLocation } from "./KeyRedirect";

export default function PasswordResetForm() {
  const { t } = useTranslation(["common", "account"]);
  document.title = t("account:passwordResetForm.title");
  const navigate = useNavigate();
  const dispatchToast = useToastsDispatch();

  const key = useKeyLocation();
  const [isKeyValid, setKeyValid] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordErrors, setNewPasswordErrors] = useState([]);

  const verifyKey = async (key) => {
    const response = await authAPI.verifyResetKey(key);
    if (response.ok) {
      setKeyValid(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (key) {
      verifyKey(key);
    } else {
      navigate(routes.Account.PasswordReset, { replace: true });
    }
  }, [key]);

  const passwordInput = {
    label: t("account:passwordResetForm.validKey.form.password"),
    name: "password",
    type: "password",
    value: newPassword,
    autoComplete: "new-password",
    onChange: setNewPassword,
    errors: newPasswordErrors,
  };

  const handlePasswordReset = async () => {
    setNewPasswordErrors([]);

    const response = await authAPI.resetPassword(key, newPassword);

    if (response.status === 400) {
      const data = await response.json();
      const errorsObject = mapAuthErrors(data.errors);
      setNewPasswordErrors(errorsObject.password);
    } else {
      dispatchToast({
        type: "add",
        variant: "success",
        heading: t("account:passwordResetForm.message.title"),
        subheading: t("account:passwordResetForm.message.content"),
      });
      navigate(routes.Account.Login);
    }
  };

  if (loading) return <FormLoader />;
  return (
    <Main>
      <Header>
        <Heading>
          {isKeyValid ? (
            <Trans
              t={t}
              i18nKey="account:passwordResetForm.validKey.heading"
              components={{
                emphasis: <span />,
              }}
            />
          ) : (
            <Trans
              t={t}
              i18nKey="account:passwordResetForm.unvalidKey.heading"
              components={{
                emphasis: <span />,
              }}
            />
          )}
        </Heading>
        <SubHeading>
          {isKeyValid ? null : (
            <span>
              <Trans
                t={t}
                i18nKey="account:passwordResetForm.unvalidKey.subheading"
                components={{
                  anchor: <Link to={routes.Account.PasswordReset} />,
                }}
              />
            </span>
          )}
        </SubHeading>
      </Header>
      <Content>
        {isKeyValid ? (
          <Form
            button={t("account:passwordResetForm.validKey.form.button")}
            onSubmit={handlePasswordReset}
          >
            <Input {...passwordInput} />
          </Form>
        ) : null}
      </Content>
    </Main>
  );
}
